import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, deepStringFind, Enums, inject, ModalHandle, PartStateAlert, Tooltip } from 'Mri.Web.Common';
import * as React from 'react';
import { BsBoxArrowUpRight } from '@react-icons/all-files/bs/BsBoxArrowUpRight';
import { BsClipboard } from '@react-icons/all-files/bs/BsClipboard';
import { BsDashSquare } from '@react-icons/all-files/bs/BsDashSquare';
import { BsFillDashCircleFill } from '@react-icons/all-files/bs/BsFillDashCircleFill';
import { BsFillPlusCircleFill } from '@react-icons/all-files/bs/BsFillPlusCircleFill';
import { BsListUl } from '@react-icons/all-files/bs/BsListUl';
import { BsPlusSquare } from '@react-icons/all-files/bs/BsPlusSquare';
import { LinkContainer } from 'react-router-bootstrap';
import { Button } from 'reactstrap';
import { AddPartModal } from './AddPartModal';
import { PartsStore } from './PartsStore';
import { PartStatusModal } from './PartStatusModal';
import { PartTagDataModal } from './PartTagDataModal';
import { RemovePartModal } from './RemovePartModal';

export class PartTreeItemGroup {
    constructor(model: Mri.Parts.Part) {
        this.head = model;
        if (model.parentPartGuid == undefined) this.open = true;
    }

    @bind
    @action
    public toggle(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.open = !this.open;
    }

    @computed
    public get canOpen() {
        return this.head.children != undefined;
    }

    @observable
    public head: Mri.Parts.Part;
    @observable
    public open: boolean | null = null;
}

export interface IDisplayPartTreeItemProps {
    readonly part: Mri.Parts.Part;
    readonly searchValue: string;
    readonly partNavigation?: string;
    readonly showReplace: boolean;
    readonly topLevelGuid: string;
}

@autoinject
@observer
export class DisplayPartTreeItemCard extends React.Component<IDisplayPartTreeItemProps> {
    public render() {
        return (
            <div className={this.className}>
                <div className=" d-flex align-items-center">
                    {this.props.part.parentPartGuid && this.props.part.children && this.props.part.children.length > 0 && (
                        <a className="btn btn-link" onClick={this.treeItem.toggle}>
                            {this.openItem ? <BsDashSquare /> : <BsPlusSquare />}
                        </a>
                    )}

                    <div className={!this.props.part.parentPartGuid ? 'p-2' : ''}>
                        <div className="fw-bold d-flex">
                            <div className="d-inline-block">{this.props.part.description}</div>
                            <PartStateAlert partStatus={this.props.part.partStatus} className="px-1" />
                        </div>
                        <span>{this.props.part.partNumber}</span>
                        {this.props.part.revision && <span className="ms-1">r.{this.props.part.revision}</span>}
                        <span className="ms-2">#{this.props.part.serialNumber}</span>
                    </div>

                    {!this.props.part.parentPartGuid && !this.props.showReplace && (
                        <div className="ms-auto">
                            <Tooltip message="Open parts view" delay={0}>
                                <LinkContainer exact to={`/parts/${this.props.part.partGuid}/tree`}>
                                    <Button color="primary">
                                        <BsBoxArrowUpRight />
                                    </Button>
                                </LinkContainer>
                            </Tooltip>
                        </div>
                    )}

                    {this.props.part.parentPartGuid && this.props.showReplace && (
                        <div className="ms-auto">
                            <Tooltip message="Tag Data" delay={0}>
                                <Button color="primary" onClick={this.openTagDataModal}>
                                    <BsListUl className="" />
                                </Button>
                            </Tooltip>

                            <Tooltip message="Status Log" delay={0}>
                                <Button color="primary" onClick={this.openStatusModal}>
                                    <BsClipboard />
                                </Button>
                            </Tooltip>

                            {this.props.part.isAssembly && (
                                <Tooltip message="Add Part" delay={0}>
                                    <Button color="primary" onClick={this.openAddPartModal}>
                                        <BsFillPlusCircleFill />
                                    </Button>
                                </Tooltip>
                            )}

                            <Tooltip message="Remove Part" delay={0}>
                                <Button color="primary" onClick={this.openRemovePartModal}>
                                    <BsFillDashCircleFill />
                                </Button>
                            </Tooltip>
                        </div>
                    )}
                </div>

                {this.openItem &&
                    this.partChildren &&
                    this.partChildren.map(p => (
                        <div className={this.props.part.parentPartGuid ? 'ms-4' : 'ms-auto'} key={p.partGuid}>
                            <DisplayPartTreeItemCard
                                part={p}
                                searchValue={this.props.searchValue}
                                partNavigation={this.props.partNavigation}
                                showReplace={this.props.showReplace}
                                topLevelGuid={this.props.topLevelGuid}
                            />
                        </div>
                    ))}

                {this.renderModals && (
                    <>
                        <AddPartModal
                            modalHandle={this.addPartModalHandle}
                            part={this.props.part}
                            topLevelGuid={this.props.topLevelGuid}
                        />
                        <RemovePartModal
                            modalHandle={this.removePartModalHandle}
                            part={this.props.part}
                            topLevelGuid={this.props.topLevelGuid}
                        />
                        <PartStatusModal
                            modalHandle={this.statusModalHandle}
                            partStatus={this.props.part.partStatus}
                            partGuid={this.props.part.partGuid}
                        />
                        <PartTagDataModal
                            modalHandle={this.partTagDataModalHandle}
                            partGuid={this.props.part.partGuid}
                        />
                    </>
                )}
            </div>
        );
    }

    @computed
    private get openItem() {
        let partChildren = this.props.part.children;
        if (this.props.partNavigation && partChildren) {
            partChildren = partChildren.filter(p => deepStringFind(p, this.props.partNavigation!));
        }

        const open =
            this.treeItem.open ||
            !!this.props.searchValue ||
            (this.props.partNavigation &&
                (this.props.partNavigation == this.props.part.partGuid || (partChildren && partChildren.length > 0)));

        return open;
    }

    @computed
    private get partChildren() {
        let partChildren = this.props.part.children;
        if (this.props.searchValue && partChildren) {
            partChildren = partChildren.filter(p => deepStringFind(p, this.props.searchValue));
        }

        return partChildren;
    }

    @computed
    private get treeItem() {
        return new PartTreeItemGroup(this.props.part);
    }

    @computed
    private get className() {
        let className = this.props.part.parentPartGuid ? 'list-group-item p-1' : 'card-item';
        if (this.props.partNavigation == this.props.part.partGuid) className = className + ' bg-warning';

        return className;
    }

    @bind
    @action
    private openAddPartModal() {
        this.renderModals = true;
        this.addPartModalHandle.open();
    }

    @bind
    @action
    private openRemovePartModal() {
        this.renderModals = true;
        this.removePartModalHandle.open();
    }

    @bind
    @action
    private openStatusModal() {
        this.renderModals = true;
        this.statusModalHandle.open();
    }

    @bind
    @action
    private openTagDataModal() {
        this.renderModals = true;
        this.partTagDataModalHandle.open();
    }

    private statusModalHandle = new ModalHandle();
    private addPartModalHandle = new ModalHandle();
    private removePartModalHandle = new ModalHandle();
    private partTagDataModalHandle = new ModalHandle();
    @observable private renderModals = false;
    @inject private readonly partsStore!: PartsStore;
}
